import applyLodashMixins from "@/js/utils/lodash-mixins";
applyLodashMixins();

import "@/js/utils/filter/lodash-mixin";

import { createApp } from "vue";
import { createPinia } from "pinia";
import router from "@/router";
import store from "@/store/store";

import Toast, { POSITION } from "vue-toastification";

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import VueProgressBar from "@aacassandra/vue3-progressbar";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import { dragscrollNext } from "vue-dragscroll";
import { VueShowdownPlugin } from "vue-showdown";

import bus from "@/js/plugins/bus";
import time from "@/js/plugins/time";
import text from "@/js/plugins/text";
import notify from "@/js/plugins/notify/notify";
import lifecycle from "@/js/plugins/lifecycle";
import validate from "@/js/plugins/validate";
import contact from "@/js/plugins/contact";
import search from "@/js/plugins/search";
import dialog from "@/js/plugins/dialog";
import Monitoring from "@/js/plugins/monitoring";
import rbac from "@/js/plugins/rbac";

// import VComponents
import VTheHeader from "@/components/global/VTheHeader/VTheHeader.vue";
import VButton from "@/components/global/VButton/VButton.vue";
import VModal from "@/components/global/VModal/VModal.vue";
import VCallout from "@/components/global/VCallout/VCallout.vue";
import VIcon from "@/components/global/VIcon/VIcon";
import VInput from "@/components/global/VInput/VInput.vue";
import VTextArea from "@/components/global/VTextArea/VTextArea.vue";
import VSelect from "@/components/global/VSelect/VSelect.vue";
import VTooltip from "@/components/global/VTooltip/VTooltip.vue";
import VCheckbox from "@/components/global/VCheckbox/VCheckbox.vue";
import VDropdown from "@/components/global/VDropdown/VDropdown.vue";
import VDropdownSelect from "@/components/global/VDropdownSelect.vue";
import VUser from "@/components/global/VUser/VUser.vue";
import VLoadingView from "@/components/global/VLoadingView/VLoadingView.vue";
import VTrackingLinkButton from "@/components/global/VTrackingLinkButton/VTrackingLinkButton.vue";
import VExpandableListRow from "@/components/global/VExpandableListRow/VExpandableListRow.vue";
import VSelectAll from "@/components/global/VSelectAll/VSelectAll.vue";
import {
	KIcon,
	KButton,
	KButtonGroup,
	KPill,
	KForm,
	KFormActions,
	KFormCheckbox,
	KFormLabel,
	KFormRow,
	KFormRadio,
	KFormRadioOption,
	KSelect,
	KTextArea,
	KTextField,
	KFormControl,
	KTextBox,
	KAccordion,
	KDropdownSingle,
	KDropdownSingleSearch,
	KDropdownMulti,
	KDropdownMultiSearch,
	KDropdownDateTimeRange,
	KDateTimePicker,
	KCheckbox,
	KRadio,
	KSearchBox,
	KTooltip
} from "@kongsberg-satellite-services/kcl";

import clickOutside from "@/js/directives/clickOutside";

import {
	progressBarConfig,
	showdownConfig
} from "@/js/utils/application-config";

// Since App.vue is dependent on some of the other imports it is imported last
import App from "@/App.vue";
import { SessionFlags } from "@/js/utils/session";

const mountApp = () => {
	const pinia = createPinia();

	const app = createApp(App)
		.component("VButton", VButton)
		.component("VInput", VInput)
		.component("VTextArea", VTextArea)
		.component("VSelect", VSelect)
		.component("VModal", VModal)
		.component("VCallout", VCallout)
		.component("VIcon", VIcon)
		.component("VTheHeader", VTheHeader)
		.component("VTooltip", VTooltip)
		.component("VCheckbox", VCheckbox)
		.component("VDropdown", VDropdown)
		.component("VDropdownSelect", VDropdownSelect)
		.component("VUser", VUser)
		.component("VLoadingView", VLoadingView)
		.component("VTrackingLinkButton", VTrackingLinkButton)
		.component("VExpandableListRow", VExpandableListRow)
		.component("VSelectAll", VSelectAll)
		.component("KIcon", KIcon)
		.component("KButton", KButton)
		.component("KButtonGroup", KButtonGroup)
		.component("KPill", KPill)
		.component("KForm", KForm)
		.component("KFormActions", KFormActions)
		.component("KFormCheckbox", KFormCheckbox)
		.component("KFormLabel", KFormLabel)
		.component("KFormRow", KFormRow)
		.component("KFormRadio", KFormRadio)
		.component("KFormRadioOption", KFormRadioOption)
		.component("KSelect", KSelect)
		.component("KTextArea", KTextArea)
		.component("KTextField", KTextField)
		.component("KFormControl", KFormControl)
		.component("KTextBox", KTextBox)
		.component("KAccordion", KAccordion)
		.component("KDropdownSingle", KDropdownSingle)
		.component("KDropdownSingleSearch", KDropdownSingleSearch)
		.component("KDropdownMulti", KDropdownMulti)
		.component("KDropdownMultiSearch", KDropdownMultiSearch)
		.component("KDropdownDateTimeRange", KDropdownDateTimeRange)
		.component("KDateTimePicker", KDateTimePicker)
		.component("KCheckbox", KCheckbox)
		.component("KRadio", KRadio)
		.component("KSearchBox", KSearchBox)
		.component("KTooltip", KTooltip)
		.use(Toast, { position: POSITION.BOTTOM_CENTER })
		.use(VueProgressBar, progressBarConfig)
		.use(VueShowdownPlugin, showdownConfig)
		.use(pinia)
		.use(store)
		.use(router)
		.use(bus)
		.use(time)
		.use(text)
		.use(notify as any)
		.use(lifecycle)
		.use(validate)
		.use(contact)
		.use(search)
		.use(dialog)
		.use(Monitoring, {
			trackInitialPageView: false,
			router
		})
		.use(rbac)
		.directive("click-outside", clickOutside)
		.directive("dragscroll", dragscrollNext);

	app.mount("#app");
};

export const bootstrapApp = async (initializeSession: () => Promise<void>) => {
	window.RuntimeFlags = new Map<string, any>();

	try {
		await initializeSession();
		mountApp();
	} catch (error) {
		console.error(error);
		window.alert("Failed to start KOGNI");
	}
};

export const initializeSessionFlags = () => {
	SessionFlags.tryInit("OVERRIDE_RESTRICTIONS", false, "data");
	SessionFlags.tryInit(
		"OVERRIDE_RESTRICTIONS",
		{
			type: "boolean",
			nullable: false
		},
		"schema"
	);

	SessionFlags.tryInit("SHOW_OTHER_KSAT_IDS", false, "data");
	SessionFlags.tryInit("SHOW_OTHER_KSAT_IDS", { type: "boolean" }, "schema");
};
